import React, { useEffect, useState } from "react";
import api, { fmtInt, fmtPct, API } from "@/api";
import { useI18n } from "@/i18n";
import { useAuth, can } from "@/auth";
import { PageHeader, Tile, Loading } from "@/components/Bits";
import { CommentsThread } from "@/components/Extras";
import ImportExportBar from "@/components/ImportExportBar";
import { useCountry } from "@/country";
import { FilePdf, Warning, Star, Plus, PencilSimple, Trash, X, ChatCircleDots } from "@phosphor-icons/react";

const EMPTY = {
  name: "", title: "", priority: "medium",
  strengths_en: "", strengths_fr: "", risks_en: "", risks_fr: "",
  milestones: [],
  total_units: "", wabtec_units: "", share_pct: "", addressable: "",
};

export default function AccountPlans() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const { code: country } = useCountry();
  const editable = can(user, "admin");
  const [plans, setPlans] = useState(null);
  const [open, setOpen] = useState(false);
  const [editingName, setEditingName] = useState(null); // original name being edited
  const [form, setForm] = useState(EMPTY);
  const [commentsFor, setCommentsFor] = useState(null);

  const load = () => api.get("/account_plans").then((r) => setPlans(r.data.rows));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({ ...EMPTY, milestones: [{ period: "", action: "" }] });
    setEditingName(null);
    setOpen(true);
  };
  const openEdit = (p) => {
    setForm({
      ...EMPTY,
      ...p,
      milestones: p.milestones && p.milestones.length ? p.milestones : [{ period: "", action: "" }],
      total_units: p.total_units ?? "",
      wabtec_units: p.wabtec_units ?? "",
      share_pct: p.share_pct ?? "",
      addressable: p.addressable ?? "",
    });
    setEditingName(p.name);
    setOpen(true);
  };
  const close = () => { setOpen(false); setForm(EMPTY); setEditingName(null); };

  const save = async () => {
    const body = {
      ...form,
      total_units: form.total_units === "" ? null : parseInt(form.total_units),
      wabtec_units: form.wabtec_units === "" ? null : parseInt(form.wabtec_units),
      share_pct: form.share_pct === "" ? null : parseFloat(form.share_pct),
      addressable: form.addressable === "" ? null : parseInt(form.addressable),
      milestones: (form.milestones || []).filter((m) => m.period || m.action),
    };
    if (!body.name) return;
    if (editingName) await api.put(`/account_plans/${encodeURIComponent(editingName)}`, body);
    else await api.post("/account_plans", body);
    close();
    await load();
  };

  const del = async (name) => {
    if (!confirm(t("ap.confirmDelete"))) return;
    await api.delete(`/account_plans/${encodeURIComponent(name)}`);
    await load();
  };

  if (!plans) return <Loading />;

  const priorityColor = (p) =>
    p === "high" ? "pill-hot" : p === "medium" ? "pill-warm" : "pill-cold";

  return (
    <div data-testid="account-plans-page">
      <PageHeader
        title={t("ap.title")}
        subtitle={t("ap.subtitle")}
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <ImportExportBar
              entity="account_plans"
              country={country}
              canImport={editable}
              previewCols={[
                { key: "name", label: "Name" },
                { key: "priority", label: "Priority" },
                { key: "total_units", label: "Total" },
                { key: "wabtec_units", label: "Wabtec" },
              ]}
              onImported={() => load()}
            />
            {editable && (
              <button onClick={openNew} className="btn-accent flex items-center gap-2" data-testid="ap-new">
                <Plus size={14} weight="bold" /> {t("ap.newBtn")}
              </button>
            )}
          </div>
        }
      />
      <div className="p-4 md:p-10 space-y-5 md:space-y-6 stagger">
        {plans.map((p) => (
          <Tile key={p.name} testId={`plan-${p.name}`}>
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <div className={`pill ${priorityColor(p.priority)} mb-2 inline-block`} data-testid={`ap-prio-${p.name}`}>
                  {t("ap.priority")}: {(p.priority || "medium").toUpperCase()}
                </div>
                <h3 className="text-[22px] font-semibold tracking-tight" style={{ letterSpacing: "-0.025em" }}>{p.name}</h3>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={`${API}/account_plans/${encodeURIComponent(p.name)}/pdf?lang=${lang}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost flex items-center gap-2"
                  data-testid={`pdf-${p.name}`}
                >
                  <FilePdf size={14} />
                  {t("ap.exportPdf")}
                </a>
                {editable && (
                  <>
                    <button onClick={() => openEdit(p)} className="btn-ghost flex items-center gap-2" data-testid={`ap-edit-${p.name}`}>
                      <PencilSimple size={14} /> {t("common.edit")}
                    </button>
                    <button onClick={() => del(p.name)} className="p-2 rounded-full hover:bg-[var(--bg-subtle)]" data-testid={`ap-del-${p.name}`}>
                      <Trash size={14} style={{ color: "var(--accent)" }} />
                    </button>
                  </>
                )}
                <button onClick={() => setCommentsFor(p.name)} className="btn-ghost flex items-center gap-2" data-testid={`ap-comments-${p.name}`}>
                  <ChatCircleDots size={14} /> {t("comments.open")}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <KPIBox label="Total" value={fmtInt(p.total_units)} />
              <KPIBox label="Wabtec" value={fmtInt(p.wabtec_units)} accent />
              <KPIBox label="Share" value={fmtPct(p.share_pct)} />
              <KPIBox label="Addressable" value={fmtInt(p.addressable)} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <div className="p-5 rounded-[12px]" style={{ background: "var(--bg-subtle)" }}>
                <div className="label-tech flex items-center gap-2 mb-2" style={{ color: "var(--success)" }}>
                  <Star size={12} weight="fill" /> {t("ap.strengths")}
                </div>
                <p className="text-[13.5px] leading-relaxed" style={{ color: "var(--text-primary)" }}>
                  {(lang === "en" ? p.strengths_en : p.strengths_fr) || p.strengths_en || <span style={{ color: "var(--text-tertiary)" }}>—</span>}
                </p>
              </div>
              <div className="p-5 rounded-[12px]" style={{ background: "var(--bg-subtle)" }}>
                <div className="label-tech flex items-center gap-2 mb-2" style={{ color: "var(--warning)" }}>
                  <Warning size={12} weight="fill" /> {t("ap.risks")}
                </div>
                <p className="text-[13.5px] leading-relaxed" style={{ color: "var(--text-primary)" }}>
                  {(lang === "en" ? p.risks_en : p.risks_fr) || p.risks_en || <span style={{ color: "var(--text-tertiary)" }}>—</span>}
                </p>
              </div>
            </div>

            <div>
              <div className="label-tech mb-3">{t("ap.milestones")}</div>
              {p.milestones && p.milestones.length > 0 ? (
                <div>
                  {p.milestones.map((m, i) => (
                    <div key={i} className="grid grid-cols-[110px_1fr] gap-4 py-2.5" style={{ borderTop: i === 0 ? "none" : "1px solid var(--border)" }}>
                      <div className="font-mono text-[12px]" style={{ color: "var(--text-secondary)" }}>{m.period}</div>
                      <div className="text-[13.5px]">{m.action}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[13px]" style={{ color: "var(--text-tertiary)" }}>{t("ap.noMilestones")}</div>
              )}
            </div>
          </Tile>
        ))}
        {plans.length === 0 && (
          <Tile>
            <div className="text-center py-10">
              <div className="label-tech" style={{ color: "var(--text-tertiary)" }}>{t("ap.empty")}</div>
              {editable && (
                <button onClick={openNew} className="btn-accent mt-4" data-testid="ap-new-empty">
                  {t("ap.createFirst")}
                </button>
              )}
            </div>
          </Tile>
        )}
      </div>

      {open && (
        <PlanModal
          form={form}
          setForm={setForm}
          onClose={close}
          onSave={save}
          editingName={editingName}
          t={t}
        />
      )}

      {commentsFor && (
        <>
          <div className="fixed inset-0 z-40" style={{ background: "rgba(0,0,0,0.35)", backdropFilter: "blur(4px)" }} onClick={() => setCommentsFor(null)} />
          <CommentsThread
            entityType="account_plan"
            entityId={commentsFor}
            onClose={() => setCommentsFor(null)}
          />
        </>
      )}
    </div>
  );
}

function KPIBox({ label, value, accent }) {
  return (
    <div className="p-4 rounded-[10px]" style={{ background: "var(--bg-subtle)" }}>
      <div className="label-tech">{label}</div>
      <div className="num text-[22px] font-semibold mt-1" style={{ color: accent ? "var(--accent)" : "var(--text-primary)", letterSpacing: "-0.025em" }}>
        {value}
      </div>
    </div>
  );
}

function PlanModal({ form, setForm, onClose, onSave, editingName, t }) {
  const updateMs = (i, field, v) => {
    const next = [...form.milestones];
    next[i] = { ...next[i], [field]: v };
    setForm({ ...form, milestones: next });
  };
  const addMs = () => setForm({ ...form, milestones: [...form.milestones, { period: "", action: "" }] });
  const removeMs = (i) => setForm({ ...form, milestones: form.milestones.filter((_, j) => j !== i) });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
    >
      <div
        className="tile p-5 md:p-7 w-full max-w-3xl fade-in"
        onClick={(e) => e.stopPropagation()}
        data-testid="ap-modal"
        style={{ boxShadow: "var(--shadow-lg)", maxHeight: "90vh", display: "flex", flexDirection: "column" }}
      >
        <div className="flex items-start justify-between mb-5">
          <h3 className="text-[22px] font-semibold tracking-tight" style={{ letterSpacing: "-0.025em" }}>
            {editingName ? `${t("ap.editTitle")} "${editingName}"` : t("ap.newTitle")}
          </h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[var(--bg-subtle)]">
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto pr-2 space-y-5" style={{ flex: 1 }}>
          <div className="grid grid-cols-2 gap-3">
            <FieldText label={t("ap.nameUnique")} v={form.name} set={(v) => setForm({ ...form, name: v })} testId="ap-name" />
            <FieldSel label={t("ap.priority")} v={form.priority} set={(v) => setForm({ ...form, priority: v })} opts={["high", "medium", "low"]} />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <FieldText label={t("ap.totalUnits")} type="number" v={form.total_units} set={(v) => setForm({ ...form, total_units: v })} />
            <FieldText label={t("ap.wabtecUnits")} type="number" v={form.wabtec_units} set={(v) => setForm({ ...form, wabtec_units: v })} />
            <FieldText label={t("ap.sharePct")} type="number" v={form.share_pct} set={(v) => setForm({ ...form, share_pct: v })} />
            <FieldText label={t("ap.addressable")} type="number" v={form.addressable} set={(v) => setForm({ ...form, addressable: v })} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldArea label={t("ap.strengthsEn")} v={form.strengths_en} set={(v) => setForm({ ...form, strengths_en: v })} />
            <FieldArea label={t("ap.strengthsFr")} v={form.strengths_fr} set={(v) => setForm({ ...form, strengths_fr: v })} />
            <FieldArea label={t("ap.risksEn")} v={form.risks_en} set={(v) => setForm({ ...form, risks_en: v })} />
            <FieldArea label={t("ap.risksFr")} v={form.risks_fr} set={(v) => setForm({ ...form, risks_fr: v })} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="label-tech">{t("ap.milestones")}</div>
              <button onClick={addMs} className="btn-ghost text-[12px] inline-flex items-center gap-1" data-testid="ap-add-ms">
                <Plus size={12} weight="bold" /> {t("ap.addRow")}
              </button>
            </div>
            <div className="space-y-2">
              {form.milestones.map((m, i) => (
                <div key={i} className="grid grid-cols-[140px_1fr_36px] gap-2" data-testid={`ap-ms-${i}`}>
                  <input
                    className="input-tech"
                    placeholder="2026 Q1"
                    value={m.period || ""}
                    onChange={(e) => updateMs(i, "period", e.target.value)}
                  />
                  <input
                    className="input-tech"
                    placeholder="Action / milestone"
                    value={m.action || ""}
                    onChange={(e) => updateMs(i, "action", e.target.value)}
                  />
                  <button
                    onClick={() => removeMs(i)}
                    className="p-2 rounded-full hover:bg-[var(--bg-subtle)] flex items-center justify-center"
                    data-testid={`ap-rm-ms-${i}`}
                  >
                    <Trash size={14} style={{ color: "var(--accent)" }} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6 pt-4" style={{ borderTop: "1px solid var(--border)" }}>
          <button onClick={onClose} className="btn-ghost" data-testid="ap-cancel">{t("common.cancel")}</button>
          <button onClick={onSave} className="btn-primary" data-testid="ap-save">
            {editingName ? t("ap.saveChanges") : t("ap.createPlan")}
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldText({ label, v, set, type = "text", testId }) {
  return (
    <div>
      <label className="label-tech block mb-1.5">{label}</label>
      <input type={type} className="input-tech" value={v ?? ""} onChange={(e) => set(e.target.value)} data-testid={testId} />
    </div>
  );
}
function FieldSel({ label, v, set, opts }) {
  return (
    <div>
      <label className="label-tech block mb-1.5">{label}</label>
      <select className="input-tech" value={v || ""} onChange={(e) => set(e.target.value)}>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
function FieldArea({ label, v, set }) {
  return (
    <div>
      <label className="label-tech block mb-1.5">{label}</label>
      <textarea rows={3} className="input-tech" value={v ?? ""} onChange={(e) => set(e.target.value)} />
    </div>
  );
}
